/**
 * Severity helpers — translate analysis objects into the tri-state
 * severity vocabulary the dashboard UI uses (CRITICAL / WARNING / OK).
 *
 * The engine emits `score` on every check and every category. These helpers
 * map a score to a severity bucket using the same thresholds as the PDF
 * `scoreColor`/`severityColor` utilities in src/shared/designTokens.js.
 */

export function severityFromScore(score) {
  if (score == null || Number.isNaN(score)) return 'OK';
  if (score >= 80) return 'OK';
  if (score >= 55) return 'WARNING';
  return 'CRITICAL';
}

/**
 * Derive a severity from an engine-emitted canonical CheckResult (§A1).
 * Priority:
 *   1. Explicit `severity` field if present (topIssues entries provide this).
 *   2. status_threshold === 'PASS' → OK.
 *   3. auto_top_issue (canonical aux) → CRITICAL.
 *   4. Fall back to the score-based bucketing.
 */
export function severityFromCheck(check) {
  if (!check) return 'OK';
  if (check.severity) return check.severity;
  if (check.status_threshold === 'PASS') return 'OK';
  if (check.auto_top_issue) return 'CRITICAL';
  return severityFromScore(check.score);
}

export function labelForSeverity(severity) {
  switch (severity) {
    case 'CRITICAL': return 'CRITICAL';
    case 'WARNING':  return 'WARNING';
    case 'LOW':      return 'LOW';
    case 'OK':       return 'PASS';
    default:         return severity || '';
  }
}

/**
 * CSS class suffix used throughout the dashboard stylesheet, e.g.
 * `badge badge-crit`, `bar-fill bar-crit`.
 */
export function severityClass(severity) {
  switch (severity) {
    case 'CRITICAL': return 'crit';
    case 'WARNING':  return 'warn';
    case 'LOW':      return 'pass';
    case 'OK':       return 'pass';
    default:         return 'na';
  }
}

/**
 * MOBILE-FIT F3 (Rule 12): the ONE verdict → severity mapping the report health
 * page uses (VerdictBox / HealthOverview STATUS_SEVERITY), given a canonical
 * home so the portfolio tile and the recent-reports chip colour their verdict
 * badges the SAME way — instead of `verdict-${status.toLowerCase()}`, which
 * produced dead classes (`verdict-approved`, `verdict-corrections required`)
 * and left every non-REJECTED badge on the grey fallback.
 */
export const VERDICT_SEVERITY = {
  APPROVED: 'OK',
  'CORRECTIONS REQUIRED': 'WARNING',
  REJECTED: 'CRITICAL',
};

/**
 * verdict string → severity CSS suffix (crit/warn/pass), via the shared map.
 * @param {string} verdict
 * @returns {'crit'|'warn'|'pass'|'na'}
 */
export function verdictClass(verdict) {
  return severityClass(VERDICT_SEVERITY[verdict] || (verdict ? 'WARNING' : undefined));
}
